import { useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { BrowserAuthError, InteractionStatus } from '@azure/msal-browser';
import { isMsalConfigured, loginRequest } from '../lib/msalConfig';

export type SigninDesign = 'random' | 'split-panel' | 'photogenic';
export const SIGNIN_DESIGN_STORAGE_KEY = 'signin_design_preference';

type Variant = 'split-panel' | 'photogenic';

function readStoredPreference(): SigninDesign {
  if (typeof localStorage === 'undefined') return 'random';
  const v = localStorage.getItem(SIGNIN_DESIGN_STORAGE_KEY);
  return v === 'split-panel' || v === 'photogenic' || v === 'random' ? v : 'random';
}

function resolveVariant(pref: SigninDesign): Variant {
  if (pref === 'split-panel' || pref === 'photogenic') return pref;
  return Math.random() < 0.5 ? 'split-panel' : 'photogenic';
}

const HERO_SPLIT_PANEL = '/signin/hero.jpg';   // corporate aerial — Design 1
const HERO_PHOTOGENIC  = '/signin/hero1.jpg';  // on-the-ground field — Design 4
const LOGO_URL = '/signin/sld-logo.png';

export function SignIn() {
  const { instance, inProgress } = useMsal();
  const [error, setError] = useState<string | null>(null);

  // Pick the variant once per mount so we don't reshuffle on every render.
  const variant = useMemo<Variant>(() => resolveVariant(readStoredPreference()), []);

  const busy = inProgress !== InteractionStatus.None;

  const handleSignIn = async () => {
    setError(null);
    if (!isMsalConfigured) {
      setError(
        'Microsoft sign-in is not configured in this environment. Set VITE_ENTRA_CLIENT_ID, VITE_ENTRA_TENANT_ID, and VITE_API_SCOPE in your .env file.',
      );
      return;
    }
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      // `interaction_in_progress` means a prior redirect was interrupted and
      // MSAL still has stale state in sessionStorage. Clearing the marker and
      // retrying once gets the user unstuck without forcing a full reload.
      if (
        err instanceof BrowserAuthError &&
        err.errorCode === 'interaction_in_progress'
      ) {
        try {
          sessionStorage.removeItem('msal.interaction.status');
        } catch {
          /* sessionStorage unavailable — fall through to error display */
        }
        try {
          await instance.loginRedirect(loginRequest);
          return;
        } catch (retryErr) {
          console.error('[SignIn] loginRedirect retry failed:', retryErr);
          setError(
            'Sign-in was interrupted. Please refresh the page and try again.',
          );
          return;
        }
      }
      console.error('[SignIn] loginRedirect failed:', err);
      setError('Sign-in failed. Please try again.');
    }
  };

  return (
    <div className="signin-root">
      <style>{SIGNIN_CSS}</style>
      <div className={`signin-page ${variant}`}>
        <aside className="signin-brand">
          <div className="signin-logo-row">
            <img className="signin-brand-logo" src={LOGO_URL} alt="Site Landscape Development" />
            <span className="signin-app-tag">Irrigation Manager</span>
          </div>

          <div className="signin-brand-hero">
            <div className="signin-eyebrow">Site Landscape Development</div>
            {variant === 'split-panel' ? (
              <>
                <h1>Smart irrigation control for commercial properties.</h1>
                <p>
                  Monitor zones, schedule cycles, and track water usage across every site you manage —
                  all in one place.
                </p>
              </>
            ) : (
              <>
                <h1>Every drop. Every zone. Every site.</h1>
                <p>
                  Control commercial irrigation across the entire DFW portfolio. Adjust schedules,
                  catch leaks early, and protect every property with precision.
                </p>
              </>
            )}
          </div>

          <div className="signin-stat-row">
            <div className="signin-stat">
              <div className="signin-stat-num">{variant === 'split-panel' ? '200+' : '300+'}</div>
              <div className="signin-stat-lbl">
                {variant === 'split-panel' ? 'Field Associates' : 'SLD Associates'}
              </div>
            </div>
            <div className="signin-stat">
              <div className="signin-stat-num">15 yrs</div>
              <div className="signin-stat-lbl">Serving DFW</div>
            </div>
            <div className="signin-stat">
              <div className="signin-stat-num">24/7</div>
              <div className="signin-stat-lbl">System Monitoring</div>
            </div>
          </div>

          <div className="signin-brand-foot">
            {variant === 'photogenic' && (
              <div className="signin-photo-caption">DFW commercial property · Maintained by SLD</div>
            )}
            <div className="signin-copy">© 2026 Site Landscape Development · Lewisville, TX</div>
          </div>
        </aside>

        <main className="signin-form-panel">
          <div className="signin-form-card">
            <img className="signin-form-logo" src={LOGO_URL} alt="Site Landscape Development" />
            <h2>Sign in</h2>
            <p className="signin-sub">
              Use your Site Landscape Development Microsoft account to access the Irrigation Manager.
            </p>

            <button
              className="signin-ms-button"
              type="button"
              onClick={handleSignIn}
              disabled={busy}
            >
              <svg
                className="signin-ms-logo"
                viewBox="0 0 23 23"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect x="1" y="1" width="10" height="10" fill="#f25022" />
                <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
                <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
                <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
              </svg>
              {busy ? 'Signing in…' : 'Sign in with Microsoft'}
            </button>

            {error && <p className="signin-error">{error}</p>}

            <p className="signin-helper">
              Need access? Contact <a href="mailto:it@siteld.com">it@siteld.com</a>.
            </p>

            <div className="signin-legal">
              © 2026 Site Landscape Development · v2.4.1
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

const SIGNIN_CSS = `
.signin-root {
  --sld-green-deep: #1f3d23;
  --sld-green: #2d5a32;
  --sld-green-bright: #6fae3d;
  --sld-ink: #1a1f1c;
  --sld-muted: #6b7368;
  --sld-border: #e3e6e1;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: var(--sld-ink);
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
  background: #fff;
}
.signin-root * { box-sizing: border-box; }

.signin-page {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  min-height: 100vh;
}

.signin-brand {
  position: relative;
  background-color: var(--sld-green-deep);
  color: #fff;
  padding: 56px 64px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
}

.signin-page.split-panel .signin-brand {
  background:
    linear-gradient(135deg, rgba(31,61,35,0.82) 0%, rgba(45,90,50,0.62) 55%, rgba(20,30,22,0.88) 100%),
    url("${HERO_SPLIT_PANEL}");
  background-size: cover, cover;
  background-position: center, center;
  background-repeat: no-repeat;
}

.signin-page.photogenic .signin-brand {
  background:
    linear-gradient(135deg, rgba(31,61,35,0.78) 0%, rgba(45,90,50,0.55) 50%, rgba(20,30,22,0.85) 100%),
    url("${HERO_PHOTOGENIC}");
  background-size: cover, cover;
  background-position: center, center;
  background-repeat: no-repeat;
}
.signin-page.photogenic .signin-brand::before {
  content: "";
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse at 30% 20%, rgba(111,174,61,0.18), transparent 55%),
    radial-gradient(ellipse at 80% 90%, rgba(0,0,0,0.45), transparent 60%);
  pointer-events: none;
}
.signin-page.photogenic .signin-brand::after {
  content: "";
  position: absolute; left: 0; right: 0; bottom: 0; height: 55%;
  background: linear-gradient(to top, rgba(20,30,22,0.85), transparent);
  pointer-events: none;
}

.signin-page.split-panel .signin-brand::before {
  content: "";
  position: absolute; inset: 0;
  background-image:
    radial-gradient(circle at 20% 30%, rgba(111,174,61,0.18) 0, transparent 40%),
    radial-gradient(circle at 80% 70%, rgba(0,0,0,0.45) 0, transparent 55%);
  pointer-events: none;
}

.signin-logo-row {
  display: flex; align-items: center; gap: 14px;
  position: relative; z-index: 1;
}
.signin-brand-logo {
  height: 48px; width: auto;
  filter: brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0,0,0,0.25));
}
.signin-page.photogenic .signin-brand-logo {
  filter: brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0,0,0,0.4));
}
.signin-app-tag {
  font-size: 13px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase;
  color: rgba(255,255,255,0.78);
  padding-left: 14px; margin-left: 14px;
  border-left: 1px solid rgba(255,255,255,0.25);
}
.signin-page.photogenic .signin-app-tag {
  color: rgba(255,255,255,0.82);
  border-left-color: rgba(255,255,255,0.3);
  text-shadow: 0 1px 4px rgba(0,0,0,0.35);
}

.signin-brand-hero { position: relative; z-index: 1; max-width: 440px; }
.signin-page.photogenic .signin-brand-hero { max-width: 480px; }

.signin-eyebrow {
  text-transform: uppercase; letter-spacing: 2px; font-size: 11px;
  color: var(--sld-green-bright); font-weight: 600; margin-bottom: 18px;
}
.signin-page.photogenic .signin-eyebrow {
  text-shadow: 0 1px 3px rgba(0,0,0,0.4);
}

.signin-brand-hero h1 {
  font-size: 40px; line-height: 1.1; font-weight: 700; margin-bottom: 18px;
  letter-spacing: -0.5px;
}
.signin-page.photogenic .signin-brand-hero h1 {
  font-size: 44px; line-height: 1.08;
  text-shadow: 0 2px 12px rgba(0,0,0,0.45);
}

.signin-brand-hero p {
  font-size: 16px; line-height: 1.55; color: rgba(255,255,255,0.78);
}
.signin-page.photogenic .signin-brand-hero p {
  line-height: 1.6; color: rgba(255,255,255,0.92);
  text-shadow: 0 1px 6px rgba(0,0,0,0.4);
  max-width: 440px;
}

.signin-stat-row {
  position: relative; z-index: 1;
  display: flex; gap: 36px;
}
.signin-stat-num {
  font-size: 22px; font-weight: 700; color: #fff;
}
.signin-page.photogenic .signin-stat-num {
  text-shadow: 0 1px 6px rgba(0,0,0,0.4);
}
.signin-stat-lbl {
  font-size: 12px; color: rgba(255,255,255,0.6);
  text-transform: uppercase; letter-spacing: 1.2px; margin-top: 4px;
}
.signin-page.photogenic .signin-stat-lbl { color: rgba(255,255,255,0.7); }

.signin-brand-foot {
  position: relative; z-index: 1;
  font-size: 12px; color: rgba(255,255,255,0.55);
}
.signin-page.photogenic .signin-brand-foot { color: rgba(255,255,255,0.6); }

.signin-photo-caption {
  display: flex; align-items: center; gap: 10px;
  font-size: 11px; color: rgba(255,255,255,0.65);
  text-transform: uppercase; letter-spacing: 1.5px;
}
.signin-photo-caption::before {
  content: ""; width: 24px; height: 1px; background: rgba(255,255,255,0.4);
}
.signin-photo-caption + .signin-copy { margin-top: 10px; }

/* Form panel */
.signin-form-panel {
  display: flex; align-items: center; justify-content: center;
  padding: 48px;
  background: #fff;
}
.signin-form-card { width: 100%; max-width: 400px; }
.signin-form-logo {
  height: 110px; width: auto;
  margin-bottom: 36px;
}
.signin-form-card h2 {
  font-size: 28px; font-weight: 700; letter-spacing: -0.3px; margin-bottom: 10px;
  color: var(--sld-ink);
}
.signin-sub {
  color: var(--sld-muted); font-size: 15px; margin-bottom: 36px;
  line-height: 1.5;
}

.signin-ms-button {
  width: 100%; padding: 14px 16px;
  background: #fff; color: #5e5e5e;
  border: 1px solid #8c8c8c;
  font-family: "Segoe UI", -apple-system, sans-serif;
  font-size: 15px; font-weight: 600;
  display: flex; align-items: center; justify-content: center; gap: 12px;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;
}
.signin-ms-button:hover:not(:disabled) { background: #f5f5f5; box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
.signin-ms-button:focus { outline: 2px solid var(--sld-green); outline-offset: 2px; }
.signin-ms-button:disabled { opacity: 0.6; cursor: wait; }
.signin-ms-logo { width: 21px; height: 21px; flex-shrink: 0; }

.signin-error {
  margin-top: 16px;
  padding: 10px 14px;
  background: #fdecea;
  border: 1px solid #f5c2c0;
  border-radius: 4px;
  font-size: 13px;
  color: #8a1f1f;
  text-align: center;
}

.signin-helper {
  margin-top: 24px;
  font-size: 13px; color: var(--sld-muted);
  text-align: center; line-height: 1.6;
}
.signin-helper a { color: var(--sld-green); font-weight: 600; text-decoration: none; }
.signin-helper a:hover { text-decoration: underline; }

.signin-legal {
  margin-top: 48px;
  font-size: 11px; color: var(--sld-muted);
  text-align: center;
}

/* iPad Mini portrait (744px) and below — stack vertically */
@media (max-width: 880px) {
  .signin-page { grid-template-columns: 1fr; }
  .signin-brand { padding: 40px 32px; min-height: 280px; }
  .signin-page.photogenic .signin-brand { min-height: 360px; }
  .signin-brand-hero h1 { font-size: 28px; }
  .signin-page.photogenic .signin-brand-hero h1 { font-size: 32px; }
  .signin-form-panel { padding: 32px 24px; }
  .signin-stat-row { gap: 24px; }
}

/* iPhone (~390px and below) */
@media (max-width: 480px) {
  .signin-brand { padding: 32px 22px; min-height: 240px; }
  .signin-page.photogenic .signin-brand { min-height: 300px; }
  .signin-brand-hero h1 { font-size: 24px; }
  .signin-page.photogenic .signin-brand-hero h1 { font-size: 24px; }
  .signin-brand-hero p { font-size: 14px; }
  .signin-app-tag { font-size: 11px; padding-left: 10px; margin-left: 10px; }
  .signin-brand-logo { height: 36px; }
  .signin-stat-row { gap: 18px; }
  .signin-stat-num { font-size: 18px; }
  .signin-stat-lbl { font-size: 10px; }
  .signin-form-panel { padding: 28px 20px; }
  .signin-form-logo { height: 72px; margin-bottom: 24px; }
  .signin-form-card h2 { font-size: 22px; }
  .signin-sub { font-size: 14px; margin-bottom: 26px; }
  .signin-ms-button { padding: 13px 14px; font-size: 14px; }
  .signin-legal { margin-top: 32px; }
}
`;
